import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  BinanceCentralization,
  BinanceCentralizationListFilters,
  BinanceCentralizationListResponse,
  CentralizationDashboardResponse,
  ManualCentralizationResponse,
} from '../interfaces/binance-centralization.interface';

/**
 * Consulta el módulo de Centralizaciones de Binance Link. Todos los
 * endpoints requieren JWT Cognito con grupo `aguacate_operator`.
 *
 * Flujo backend: depósito on-chain → cron cada 5 min transfiere de la
 * sub-cuenta al master → acredita en cardex del usuario. Este servicio
 * solo lee resultados, no dispara el flujo.
 */
@Injectable({
  providedIn: 'root',
})
export class BinanceCentralizationService {
  private baseUrl = `${environment.apiNest}binance-link/centralizations`;

  constructor(private http: HttpClient) {}

  /** GET /binance-link/centralizations/dashboard — KPIs agregados. */
  getDashboard(): Promise<CentralizationDashboardResponse> {
    return firstValueFrom(
      this.http.get<CentralizationDashboardResponse>(`${this.baseUrl}/dashboard`),
    );
  }

  /** GET /binance-link/centralizations — listado paginado con filtros. */
  list(
    filters: BinanceCentralizationListFilters = {},
  ): Promise<BinanceCentralizationListResponse> {
    let params = new HttpParams();
    if (filters.page != null) params = params.set('page', String(filters.page));
    if (filters.limit != null) params = params.set('limit', String(filters.limit));
    if (filters.status) params = params.set('status', filters.status);
    if (filters.usuarioId != null)
      params = params.set('usuarioId', String(filters.usuarioId));
    if (filters.walletId) params = params.set('walletId', filters.walletId);
    if (filters.subAccountId)
      params = params.set('subAccountId', filters.subAccountId);
    if (filters.txId) params = params.set('txId', filters.txId);
    if (filters.coin) params = params.set('coin', filters.coin);
    if (filters.dateFrom) params = params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params = params.set('dateTo', filters.dateTo);
    if (filters.q) params = params.set('q', filters.q);
    return firstValueFrom(
      this.http.get<BinanceCentralizationListResponse>(this.baseUrl, { params }),
    );
  }

  /** GET /binance-link/centralizations/:id — detalle por UUID. */
  getById(id: string): Promise<BinanceCentralization> {
    return firstValueFrom(
      this.http.get<BinanceCentralization>(`${this.baseUrl}/${id}`),
    );
  }

  /** GET /binance-link/centralizations/by-tx-id/:txId — lookup por hash on-chain. */
  getByTxId(txId: string): Promise<BinanceCentralization> {
    return firstValueFrom(
      this.http.get<BinanceCentralization>(`${this.baseUrl}/by-tx-id/${txId}`),
    );
  }

  /**
   * POST /binance-link/centralizations/wallet/:walletId/run-manual
   *
   * Fuerza la centralización manual de depósitos USDT pendientes en una
   * sub-cuenta Binance. Caso típico: wallets con `kycStatus = REJECT`
   * cuyo cron automático no las procesa.
   *
   * Idempotente: depósitos ya centralizados antes no se duplican.
   *
   * Latencia esperada: hasta ~30s por depósito (transfer Binance + tx
   * local). Para sub-cuentas con muchos depósitos pueden ser varios
   * minutos — el caller debería deshabilitar el botón mientras la
   * promesa está en vuelo y NO timeoutear antes de 5 min.
   *
   * @throws 404 si el `walletId` no existe.
   * @throws 409 si `subAccountId` todavía es placeholder (`pending-*`).
   * @throws 500 si falta env `BINANCE_KYC_SAAS_BROKER_MAIN`.
   * @throws 502 ante error de red contra Binance.
   */
  runManualCentralization(
    walletId: string,
  ): Promise<ManualCentralizationResponse> {
    return firstValueFrom(
      this.http.post<ManualCentralizationResponse>(
        `${this.baseUrl}/wallet/${walletId}/run-manual`,
        {},
      ),
    );
  }
}
